/**
 * BreadcrumbContext (Iter236 Phase 2)
 *
 * Deep-link pages can set a dynamic label that overrides the last
 * breadcrumb crumb text — e.g. `/cases/:id` sets `Case: ABC-123`
 * instead of just `Cases`. Pages call `useSetBreadcrumbLabel(label)`
 * on mount (usually once data has loaded). Header consumes
 * `useBreadcrumbLabel()` and if the value is non-null, replaces the
 * final crumb title.
 *
 * The value auto-clears on route change so stale labels don't bleed
 * between pages.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { useLocation } from 'react-router-dom';

const BreadcrumbCtx = createContext({
  label: null,
  setLabel: () => {},
});

export function BreadcrumbProvider({ children }) {
  const location = useLocation();
  const [label, setLabelState] = useState(null);

  // Clear on route change (path only — search-string changes shouldn't wipe it).
  useEffect(() => {
    setLabelState(null);
  }, [location.pathname]);

  const setLabel = useCallback((next) => {
    setLabelState(next || null);
  }, []);

  const value = useMemo(() => ({ label, setLabel }), [label, setLabel]);
  return (
    <BreadcrumbCtx.Provider value={value}>{children}</BreadcrumbCtx.Provider>
  );
}

export function useBreadcrumbLabel() {
  return useContext(BreadcrumbCtx).label;
}

/**
 * Convenience hook: page calls this on data-loaded with the final
 * crumb text. Passing `null` (or nothing) clears the override.
 */
export function useSetBreadcrumbLabel(label) {
  const { setLabel } = useContext(BreadcrumbCtx);
  useEffect(() => {
    setLabel(label || null);
    return () => setLabel(null);
  }, [label, setLabel]);
}
